import { type PaletteKey, type Pixel, PixelCanvas, remap } from '../lib';

/**
 * The player's body, Storybook direction, at 4× pixel density (ADR-0073): 64×72 art px drawn at a quarter scale, so he
 * stands 16×18 world px like the 1× original, and at 1080p one art px is one device px. Origin [32,71]: soles end on
 * row 69, rows 70-71 are the outline under them. No arms: the weapon sprite carries the hand (grip at art (36,55) =
 * world (+1,-4) from the origin), so the gun hides most of the torso and the team colour must also read on the legs,
 * hat band and ribbon.
 *
 * Every frame is composed from parts drawn without outline (ribbon, legs, torso, the face and hair of the look, hat),
 * then sel-outlined as one shape, 2 art px thick (half a world px: the outline's weight of the 1× and 2× gardeners),
 * so the silhouette stays continuous whatever the pose. Detail inside is 1 art px. The hair covers the scalp rows
 * under the brim, so a lagging hat never opens a hole.
 *
 * Each part is authored in its default colours and recoloured before compositing (index.ts): the hat by its palette
 * (straw `y Y U e t`) and the team (`b B c`: band, ribbon, overalls), the face by the skin, the hair by its colour.
 * The outline is picked from the final colours, so the table below covers every colour a look, hat or team can use;
 * under the hat it follows the straw, so every hat keeps the straw's brown rim.
 *
 * Redrawn natively from Scale2x of the 2× art (2026-09): straw weave rings and a crown sheen, a band with a lit edge
 * and a shade line on either side, a tee with a chin shadow, straps, grey buttons and a bevelled pocket, legs with 1-px
 * lit edges, rolled cuffs and boots with a lit toe cap, a satin ribbon with a forked end, a daisy of 8 petals.
 * Rubric (default look, preview + `looks` sheet + scene): S5 R4 P4 L4 C4 A4 F4. Still weak: the straw has 5 tones (weave `t`); hair strands and weave add texture
 * that thins out at world ×2; the dead pose's head and hair hide most of the body.
 */
export const grid = (src: string) => PixelCanvas.fromGrid(src);

export type Recolor = Partial<Record<PaletteKey, PaletteKey>>;
/** A part: its pixels and its top-left in the 64×72 frame (standing still: no bob, lean or lag). */
export type Part = readonly [canvas: PixelCanvas, x: number, y: number];

export const recolored = (c: PixelCanvas, map: Recolor): PixelCanvas =>
  Object.keys(map).length ? remap(c.clone(), map) : c;
export const recoloredPart = ([c, x, y]: Part, map: Recolor): Part => [recolored(c, map), x, y];

/** Paints parts in order onto one canvas that covers them all (a beard or glasses reach past the hair's). */
export function stack(...parts: Part[]): Part {
  if (parts.length === 1) return parts[0]!;
  const x0 = Math.min(...parts.map(([, x]) => x));
  const y0 = Math.min(...parts.map(([, , y]) => y));
  const x1 = Math.max(...parts.map(([c, x]) => x + c.w));
  const y1 = Math.max(...parts.map(([c, , y]) => y + c.h));
  const c = new PixelCanvas(x1 - x0, y1 - y0);
  for (const [p, x, y] of parts) c.blit(p, x - x0, y - y0);
  return [c, x0, y0];
}

/**
 * Outline tones per final colour: [lit edge (top/left), shadow edge (right), ground edge (below; default = shadow)].
 * Every tone sits well below the ground's value (grass g, lane m) so the figure keeps a dark rim on any floor: straw,
 * skin, blonde and ginger rims are the darkest browns (a U/u rim had the grass's own value and vanished in greyscale).
 */
const OUTLINE: Partial<Record<PaletteKey, readonly [PaletteKey, PaletteKey, PaletteKey?]>> = {
  // straw, skin, warm hair
  e: ['n', 'N'],
  y: ['n', 'N'],
  Y: ['n', 'N'],
  U: ['n', 'N'],
  t: ['n', 'N'],
  o: ['n', 'N'],
  u: ['n', 'N'],
  s: ['n', 'N'],
  S: ['n', 'N'],
  a: ['N', 'N', 'k'],
  m: ['n', 'N'],
  n: ['N', 'N', 'k'],
  N: ['N', 'k', 'k'],
  // tee, silver and black hair, the midnight hat
  w: ['3', '2'],
  '5': ['3', '2'],
  '4': ['2', '1'],
  '3': ['2', '1'],
  '2': ['1', 'k', 'k'],
  '1': ['k', 'k'],
  // teams
  c: ['B', '1'],
  b: ['B', '1'],
  B: ['1', '1'],
  r: ['R', '1'],
  R: ['1', '1'],
  // pink hair, lavender hat, red team's light
  i: ['P', 'N'],
  p: ['P', 'N'],
  P: ['N', 'k'],
  // daisy stem, mint hat
  l: ['G', 'D'],
  g: ['G', 'D'],
  G: ['D', 'D'],
};

/**
 * Paints every transparent pixel for which `pick` answers, given the indices of its neighbours above, left, right and
 * below (-1 off the canvas or where transparent); all are read before any is written.
 */
function ring(c: PixelCanvas, pick: (up: number, left: number, right: number, down: number) => Pixel): void {
  const { w, h, pixels } = c;
  const add: number[] = [];
  const colours: Pixel[] = [];
  const at = (i: number, inside: boolean) => (inside && pixels[i] !== null ? i : -1);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (pixels[i] !== null) continue;
      const p = pick(at(i - w, y > 0), at(i - 1, x > 0), at(i + 1, x < w - 1), at(i + w, y < h - 1));
      if (p === null) continue;
      add.push(i);
      colours.push(p);
    }
  }
  for (let k = 0; k < add.length; k++) pixels[add[k]!] = colours[k]!;
}

const NO_OUTLINE = ['N', 'N'] as const;

/**
 * Sel-out, 2 art px thick: the first ring takes the outline tone of the material it touches (looked up above → left →
 * right → below), the second repeats the first, so edges read as a darker version of the part instead of flat black.
 * Pinholes and 1-px notches where two parts' rims meet are closed. Where `key` has a pixel, its colour picks the tone
 * instead: the hat's straw colours, so every hat keeps the straw's brown rim (a golden hat's white straw would take
 * the tee's slate one).
 */
export function selOutline(c: PixelCanvas, key?: PixelCanvas): void {
  const px = c.pixels;
  const keys = key?.pixels;
  ring(c, (up, left, right, down) => {
    const t = up >= 0 ? up : left >= 0 ? left : right >= 0 ? right : down;
    if (t < 0) return null;
    const [lit, shadow, ground = shadow] = OUTLINE[(keys?.[t] ?? px[t])!] ?? NO_OUTLINE;
    return t === up ? ground : t === left ? shadow : lit;
  });
  ring(c, (up, left, right, down) => {
    const t = up >= 0 ? up : left >= 0 ? left : right >= 0 ? right : down;
    return t < 0 ? null : px[t]!;
  });
  // Where two parts' rims meet they can leave a 1-px pinhole or notch: close any gap walled in on 3 sides.
  ring(c, (up, left, right, down) =>
    (up >= 0 ? 1 : 0) + (left >= 0 ? 1 : 0) + (right >= 0 ? 1 : 0) + (down >= 0 ? 1 : 0) >= 3
      ? px[up >= 0 ? up : left]!
      : null,
  );
}

// Straw sun hat: domed crown, team band, a wide brim seen from above (lit left, the crown's shadow on its right, the
// front lip and underside in U). The brim tips flex when the hat lags the head.
export const HAT_Y = 6;
export const HAT = grid(`
  ..........................yyyyyyYYYYYU..........................
  ........................yyyeeeeyyyYYYYYUU.......................
  ......................yyyeeeeeeyyyyYYYYYYUU.....................
  .....................ytyyeteeytyyytyYYUYYYuUU...................
  ....................yyyyyyeeyyyyyyyyyYYYYYYUUU..................
  ....................yyyyyyyyyyyyyyyyyYYYYYYYUUU.................
  ...................yytyyytyyytyyytyyyUYYYUYYUuU.................
  ...................yYYYYYYYYYYYYYYYYYYYYYUUUUUUU................
  ............yyyyyyyybccccccccccccccbbbbbbBBBBBBBUUUU............
  .........yyyyyyyyyyycbbbbbbbbbbbbbbbbbbbBBBBBBBBUUUUUYY.........
  ......yyyyyyyyyyyyyycbbbbbbbbbbbbbbbbbbbBBBBBBBBUUUUUUYYYU......
  .....yyyyyyyyyyyyyyybbbbbbbbbbbbbbbbbbbBBBBBBBBBUUUUUUUYYUU.....
  ....yyyyttyyyyyyyyyyYYYYYYYYYYYYUUUUUUUUUUUUUUUUUUUUUUYYYUUU....
  ....yyyyyyyyttyyyyyyyyyyyYYYYYYYYYUUUUUUUUUUUUUUUUUYUUYYYUUU....
  ....eyyyyyyyyyyyttyyyyyyyyyyyYYYYYYYYYYYYYYYuuUUuuYYYYYYYYUU....
  ....eeyyyyyyyyyyyyyyttyyttyyttyytUYYUUYYUUYYYYYYYYYYYYYYYYUU....
  .....yeeyyyyyyyyyyyyyyyyyyyyyyyyyyyYYYYYYYYYYYYYYYYYYUUUUUU.....
  ......yyeeeyyyyyyyyyyyyyyyyyyyyYYYYYYYYYYYYYYYYYYYYYUUUUUU......
  ........UUUUyyyyyyyyyyyyyYYYYYYYYYYYYYYYYYYYYYYYYYUUUUUU........
  ...........UUUUYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYUUU...........
  ................UUUUUUUUUUUUUUUUUUUUUUUUUUUUUUUU................
  .....................UUUUUUUUUUUUUUUUUUUUUU.....................
`);
/** How far each brim column flexes (in steps of the lag): the tips most, their inner neighbours by half. */
const BRIM_FLEX: Record<number, number> = { 4: 2, 5: 2, 6: 1, 7: 1, 56: 1, 57: 1, 58: 2, 59: 2 };

export type Brim = 'flat' | 'up' | 'down';

function flexBrim(brim: Brim): PixelCanvas {
  if (brim === 'flat') return HAT;
  const out = HAT.clone();
  const dir = brim === 'up' ? -1 : 1;
  for (const [col, steps] of Object.entries(BRIM_FLEX)) {
    const x = Number(col);
    for (let y = 0; y < HAT.h; y++) out.set(x, y, null);
    for (let y = 0; y < HAT.h; y++) if (HAT.get(x, y) !== null) out.set(x, y + dir * steps, HAT.get(x, y));
  }
  return out;
}
export const HATS: Record<Brim, PixelCanvas> = { flat: HAT, up: flexBrim('up'), down: flexBrim('down') };

/** Where the face (the look's skin, eyes and mouth; looks.ts) sits: its hair covers the rows under the brim. */
export const HEAD_X = 12;
export const HEAD_Y = 24;

/**
 * Leg grids: the near leg in team tones (`c b B`, boot `n N`), the far leg in placeholders (`l g G`, boot `m D`).
 * `<name>` paints the far leg in shadow; the mirrored step `<name>2` swaps them (below the hips), so the leg that was
 * behind comes forward lit.
 */
const FAR: readonly PaletteKey[] = ['l', 'g', 'G', 'm', 'D'];
function legSet(src: Record<string, string>, hipRows = 8): Record<string, Part> {
  const step: Recolor = { l: 'b', g: 'B', G: 'B', m: 'N', D: 'N' };
  const swap: Recolor = { c: 'b', b: 'B', B: 'B', n: 'N', N: 'N', l: 'c', g: 'b', G: 'B', m: 'n', D: 'N' };
  const out: Record<string, Part> = {};
  for (const [name, g] of Object.entries(src)) {
    const base = grid(g);
    for (const [suffix, map] of [
      ['', step],
      ['2', swap],
    ] as const) {
      const c = base.clone();
      for (let y = 0; y < c.h; y++) {
        for (let x = 0; x < c.w; x++) {
          const p = c.get(x, y);
          // The hips keep their own shading; only the far leg's placeholders are painted there.
          const to = p === null || (y < hipRows && !FAR.includes(p)) ? undefined : map[p];
          if (to) c.set(x, y, to);
        }
      }
      out[`${name}${suffix}`] = [c, 0, 48];
    }
  }
  return out;
}

export const LEGS = legSet({
  stand: `
    .................cbbbbbbbbbbbbbbbbbbbbbbbBB.....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbBBbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbBBBBbbbbbbbbbBB.....................
    ................cbbbbbbbBB......lgggggggGG......................
    ................cbbbbbbbBB......lgggggggGG......................
    ................cbbbbbbbBB......lgggggggGG......................
    ................cbbbbbbbBB......lgggggggGG......................
    ................cbbbbbbbBB......lgggggggGG......................
    ................ccccccccBB......llllllllGG......................
    ................cbbbbbbbBB......lgggggggGG......................
    ................BBBBBBBBBB......GGGGGGGGGG......................
    ................nNNNNNNNNN......mDDDDDDDDD......................
    ................nNNNNNNNNNN.....mDDDDDDDDDD.....................
    ................nNNNNNNNNnnnN...mDDDDDDDDmmmD...................
    ................nNNNNNNNNNNNNN..mDDDDDDDDDDDDD..................
    ................22222222222222..22222222222222..................
    .................222222222222....222222222222...................
  `,
  // contact: near leg reaching forward (heel down), far leg stretched behind on its toe
  contact: `
    .................cbbbbbbbbbbbbbbbbbbbbbbbBB.....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ...............lgggggGGbbbbbbbbbbbbbbbbbbbBB....................
    .............lggggggggGGbbbbbbbbbbbbbbbbbBB.....................
    .............lgggggggGG.........cbbbbbbbBB......................
    ...........lgggggggGG............cbbbbbbBB......................
    ...........lgggggggGG............cbbbbbbbBB.....................
    .........lgggggggGG................cbbbbbbbBB...................
    .........lgggggggGG................cbbbbbbbBB...................
    .......llllllllGG....................ccccccccBB.................
    .......lgggggggGG....................cbbbbbbbBB.................
    ......GGGGGGGGG.......................BBBBBBBBBB................
    ....mDDDDDDDDD........................nNNNNNNNNN................
    ....mDDDDDDDDD........................nNNNNNNNNNN...............
    ......mDDDDDDD........................nNNNNNNNNnnnN.............
    .......mDDDDDD........................nNNNNNNNNNNNNN............
    ........222222........................22222222222222............
    .........2222..........................222222222222.............
  `,
  // down: weight on the near leg under the body, far foot kicks up behind
  down: `
    .................cbbbbbbbbbbbbbbbbbbbbbbbBB.....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    .............lgggggGGbbbbbbbbbbbbbbbbbbbbbBB....................
    ............lgggggggGGbbbbbbbbbbbbbbbbbbbbB.....................
    .........lggggggggggGG..cbbbbbbbBB..............................
    ........lggggggggggGG...cbbbbbbbBB..............................
    .....mDDDDDlgggggggGG...cbbbbbbbBB..............................
    ....mDDDDDDDDlgggGG.....cbbbbbbbBB..............................
    ...2mDDDDDDDDD..........cbbbbbbbBB..............................
    ..222mDDDDDDD...........ccccccccBB..............................
    ..2222..................cbbbbbbbBB..............................
    ...22...................BBBBBBBBBB..............................
    ........................nNNNNNNNNN..............................
    ........................nNNNNNNNNNN.............................
    ........................nNNNNNNNNnnnN...........................
    ........................nNNNNNNNNNNNNN..........................
    ........................22222222222222..........................
    .........................222222222222...........................
  `,
  // flight: near leg pushes off behind, far knee swings forward and up
  up: `
    .................cbbbbbbbbbbbbbbbbbbbbbbbBB.....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbbbbbbbbbbbbBB....................
    ................cbbbbbbbbbbbbbbBBlgggggggggGG...................
    ...............cbbbbbbbbbbbbbbBBlggggggggggggGG.................
    ...............cbbbbbbBB........lggggggggggggGG.................
    .............cbbbbbbbBB..........lggggggggggggGG................
    .............cbbbbbbbBB...............lgggggggGG................
    ...........cbbbbbbbBB..................lggggggGG................
    ...........cbbbbbbbBB..................lggggggGG................
    .........cbbbbbbbBB.....................lgggggGG................
    .........ccccccccBB.....................llllllGG................
    ........BBBBBBBBB.......................GGGGGGGG................
    .......nNNNNNNNN........................mDDDDDDDDmmmD...........
    .....nNNNNNNNNN.........................mDDDDDDDDDDDDD..........
    .....nNNNNNNNNN.........................2222222222222222........
    ....nNNNNNNNN............................222222222222222........
    ....222222......................................................
    .....2222.......................................................
    ................................................................
    ................................................................
  `,
});

/** Team ribbon trailing from the hat band, [grid, x, y] relative to the hat: hanging at rest, streaming on the run. */
export const RIBBON = {
  hang: [
    grid(`
      ......cbbB
      .....cbbB.
      ....cbbB..
      ....cbbB..
      ...cbbB...
      ...cbbB...
      ...cbbB...
      ...cbbB...
      ...cbbBB..
      ..cbbbbB..
      ..cbbbbB..
      ..cbB.bB..
      ..cB..bB..
      ..c...B...
    `),
    2,
    16,
  ],
  hang2: [
    grid(`
      ......cbbB
      .....cbbB.
      .....cbbB.
      ....cbbB..
      ....cbbB..
      ....cbbB..
      ....cbbB..
      ....cbbB..
      ....cbbBB.
      ....cbbbbB
      ....cbbbbB
      ....cbB.bB
      ....cB..bB
      ....c...B.
    `),
    2,
    16,
  ],
  flowA: [
    grid(`
      ..............cbbB
      ..........cccbbbB.
      ....ccccbbbbbBB...
      ccccbbbbbBBBB.....
      .cbbBBBB..........
      ..BB..............
      ..................
      ..................
    `),
    -2,
    18,
  ],
  flowB: [
    grid(`
      ..............cbbB
      ...........ccbbbB.
      cc......cccbbbBB..
      .cccccbbbbbBBB....
      ..bbbbBBBBB.......
      ...BBB............
      ..................
      ..................
    `),
    -2,
    18,
  ],
  flowC: [
    grid(`
      ..............cbbB
      ..........ccbbbbB.
      .....cccbbbbbBB...
      ..ccbbbbBBBB......
      cbbbBBBB..........
      cBB...............
      .B................
      ..................
    `),
    -2,
    18,
  ],
} satisfies Record<string, Part>;
export type RibbonKey = keyof typeof RIBBON;

// Flat on his back, hatless (a hat pops off as he falls, `gardener_hat`, ADR-0036; headwear tied on stays on the head,
// ADR-0089), a daisy sprouting beside the boots: the legs to the waist (x < 24). The outfit's top lies right of them
// (outfits.ts), the head (face and hair of the look) over it: looks.ts.
export const DEAD_BODY: Part = [
  grid(`
    ....ww..................
    .w..ww..w...............
    .ww.ww.ww...............
    ..wwoow5................
    wwwoooou555.............
    wwwoooou555.............
    ..woouu5................
    .55.55.55...............
    .5..55..5...............
    ....55..................
    ....lG..................
    ....lG.....nnn..........
    .ll.lG....nmmnn.........
    llgglG....nNNNNN........
    .ggglG..23nNNNNNcccccccc
    ....lG..23nNNNNNcBbbbbbb
    ....lG..23nnnnnNcBbbbbbb
    ....lG..23nNNNNNcBbbbbbb
    ........nnnNNNNNcBbbbbbb
    .......nmmnnNNNNcBbbbbbb
    .......nNNNNNNNNBBBBBBBB
    .....23nNNNNNNNccccccccc
    .....23nNNNNNcBbbbbbbbbb
    .....23nnnnnNcBbbbbbbbbb
    .....23nNNNNNcBbbbbbbbbb
    .....23nNNNNNcBbbbbbbbbb
    .....23nNNNNNcBbbbbbbbbb
    .....23nNNNNNBBBBBBBBBBB
    ......2NNNNN..BBBBBBBBBB
    .......NNNN.............
  `),
  0,
  39,
];

interface Pose {
  legs: string;
  /** Torso + head offset (0 = standing, negative = up in the air). */
  bob: number;
  /** Extra head (+hat) offset on top of `bob` (breathing). */
  head?: number;
  /** Hat offset from its rest row (lags the head by a frame). */
  hat: number;
  /** Brim flex: 'up' tips lifted, 'down' tips drooping. */
  brim: Brim;
  /** Forward lean of the head + hat in px. */
  lean?: number;
  ribbon: RibbonKey;
  blink?: boolean;
}

/** One look's parts, recoloured (index.ts): the body's in the hat's and team's colours, the head's in the look's. */
export interface Kit {
  hat: Record<Brim, PixelCanvas>;
  /** The hat as drawn, before its palette: its rim follows these colours (the sun hat's straw in any palette). */
  hatKey: Record<Brim, PixelCanvas>;
  /**
   * Tied on (ADR-0089: a durag, a turban): the headwear is part of `hair` and its tails (`ribbon`) move with the head;
   * a hat lags it a frame.
   */
  worn: boolean;
  ribbon: Record<RibbonKey, Part>;
  legs: Record<string, Part>;
  /** The outfit's top (outfits.ts, ADR-0090; a chain on the collar, ADR-0088). */
  torso: Part;
  /** Behind everything, moving with the head: hair that hangs behind or rises round the hat (long, braids, an afro). */
  behind: readonly Part[];
  face: Part;
  blink: Part;
  /** Over the face: a beard, the fringe, sides and the scalp under the brim, then glasses or an earring (ADR-0088). */
  hair: Part;
  /** The dead pose's parts in drawing order (legs, the outfit's top, head, then hair with its beard and extras). */
  dead: readonly Part[];
}

const put = (c: PixelCanvas, [p, x, y]: Part, dx = 0, dy = 0) => c.blit(p, x + dx, y + dy);

const figure = (p: Pose, k: Kit, c: PixelCanvas): void => {
  const lean = p.lean ?? 0;
  const head = p.bob + (p.head ?? 0);
  const hatY = HAT_Y + (k.worn ? head : p.hat);
  for (const part of k.behind) put(c, part, lean, head);
  put(c, k.ribbon[p.ribbon], lean, hatY);
  put(c, k.legs[p.legs]!);
  put(c, k.torso, 0, p.bob);
  put(c, p.blink ? k.blink : k.face, lean, head);
  put(c, k.hair, lean, head);
  c.blit(k.hat[p.brim], lean, hatY);
  // The hat is on top: its rim follows the straw it was drawn in (the band never reaches the edge).
  finish(c, new PixelCanvas(c.w, c.h).blit(k.hatKey[p.brim], lean, hatY));
};

/**
 * Outlines a figure's frame (`selOutline`, `key` as there), first trimming fill that reached its border (the outline
 * needs a free 2-px ring: e.g. the brim tip when leaning).
 */
export function finish(c: PixelCanvas, key?: PixelCanvas): void {
  for (let y = 0; y < c.h; y++) for (const x of [0, 1, c.w - 2, c.w - 1]) c.set(x, y, null);
  for (let x = 0; x < c.w; x++) for (const y of [0, 1]) c.set(x, y, null);
  selOutline(c, key);
}

/** The hat follows the head one frame late (at most `maxLag` px apart) and the brim tips flex with the lag. */
function withLag(poses: Omit<Pose, 'hat' | 'brim'>[], maxLag: number): Pose[] {
  const headAt = poses.map((p) => p.bob + (p.head ?? 0));
  return poses.map((p, i) => {
    const now = headAt[i]!;
    const prev = headAt[(i + poses.length - 1) % poses.length]!;
    const hat = now + Math.max(-maxLag, Math.min(maxLag, prev - now));
    const brim: Brim = prev < now ? 'up' : prev > now ? 'down' : 'flat';
    return { ...p, hat, brim };
  });
}

// Breathing: the torso sinks and the head settles a frame later, then a blink.
const IDLE = withLag(
  [
    { legs: 'stand', bob: 0, ribbon: 'hang' },
    { legs: 'stand', bob: 0, head: 2, ribbon: 'hang' },
    { legs: 'stand', bob: 2, head: 2, ribbon: 'hang2' },
    { legs: 'stand', bob: 0, head: 2, ribbon: 'hang2' },
    { legs: 'stand', bob: 0, ribbon: 'hang', blink: true },
    { legs: 'stand', bob: 0, ribbon: 'hang' },
  ],
  2,
);
// contact → down → flight per step, bob (down 2, up 4 at the apex), leaning into the run, ribbon streaming.
const RUN = withLag(
  [
    { legs: 'contact', bob: 0, lean: 4, ribbon: 'flowA' },
    { legs: 'down', bob: 2, lean: 4, ribbon: 'flowB' },
    { legs: 'up', bob: -4, lean: 4, ribbon: 'flowC' },
    { legs: 'contact2', bob: 0, lean: 4, ribbon: 'flowA' },
    { legs: 'down2', bob: 2, lean: 4, ribbon: 'flowB' },
    { legs: 'up2', bob: -4, lean: 4, ribbon: 'flowC' },
  ],
  4,
);

/**
 * How the body stands on each frame of its animations, for what rides it without being drawn into it (flair, ADR-0160):
 * the torso's bob, the head's (with its breath: headwear tied on moves with it) and the head's lean, art px.
 */
const stance = (p: Pose) => ({ bob: p.bob, head: p.bob + (p.head ?? 0), lean: p.lean ?? 0 });
export const BODY_POSES = { idle: IDLE.map(stance), run: RUN.map(stance) } as const;

const frame = (draw: (c: PixelCanvas) => void, w = 64, h = 72): PixelCanvas => {
  const c = new PixelCanvas(w, h);
  draw(c);
  return c;
};

/** The body's frames for a kit: idle 6f, run 6f, dead 1f (64×72, outlined). */
export function bodyFrames(k: Kit): Record<'idle' | 'run' | 'dead', PixelCanvas[]> {
  return {
    idle: IDLE.map((p) => frame((c) => figure(p, k, c))),
    run: RUN.map((p) => frame((c) => figure(p, k, c))),
    dead: [
      frame((c) => {
        for (const part of k.dead) put(c, part);
        selOutline(c);
      }),
    ],
  };
}

// The sun hat alone (ADR-0036): knocked off when its gardener dies, it tumbles through the air and lies on the grass
// until they come back. `fly` is a tumble (upright → tipped → upside down, showing the dark inside → tipped the other
// way), `rest` the hat lying brim down with its ribbon trailing. The same HAT grid as on the head, same sel-out. Other
// headwear has frames of its own (headwear.ts, ADR-0089).
/** `hat` (a hat grid as worn, its top at `HAT_Y`) on its own 64×40 canvas, its top at `y`. */
export const hatAt = (y: number, hat = HAT) => {
  const c = new PixelCanvas(64, 40);
  c.blit(hat, 0, y);
  return c;
};

/**
 * The upright `hat` tipped by `deg` (positive = its right side up), rotated by three shears so every pixel survives
 * (no holes or doubled pixels, unlike sampling), around the crown's base.
 */
export function tipped(deg: number, hat = HAT): PixelCanvas {
  const src = hatAt(6, hat);
  const out = new PixelCanvas(64, 40);
  const a = Math.tan((deg * Math.PI) / 360);
  const b = -Math.sin((deg * Math.PI) / 180);
  const [cx, cy] = [32, 18];
  for (let y = 0; y < src.h; y++) {
    for (let x = 0; x < src.w; x++) {
      const p = src.get(x, y);
      if (p === null) continue;
      let dx = x - cx;
      let dy = y - cy;
      dx -= Math.round(a * dy);
      dy += Math.round(b * dx);
      dx -= Math.round(a * dy);
      out.set(cx + dx, cy + dy, p);
    }
  }
  // The shears leave stair-step spurs (a pixel with one neighbour) and notches (a hole with three): smooth them.
  const n4 = (x: number, y: number) =>
    [out.get(x - 1, y), out.get(x + 1, y), out.get(x, y - 1), out.get(x, y + 1)].filter((q) => q !== null);
  for (let y = 0; y < out.h; y++) {
    for (let x = 0; x < out.w; x++) {
      const near = n4(x, y);
      if (out.get(x, y) !== null && near.length <= 1) out.set(x, y, null);
      else if (out.get(x, y) === null && near.length >= 3) out.set(x, y, near[1]!);
    }
  }
  return out;
}

const HAT_DOWN = grid(`
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ............yyyyyyyyyyyYYYYYYYYYYYYYYYYYYYYYYYYYYUUU............
  .........yyyyyyyyyyyyYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYUUU.........
  ......yyyyyyyyyyyyyYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYUUU......
  .....yyyttyyyyyyyyyYYYUUUUUUUUUUUUUUUUUUUUYYYYYYYYYYYYYttUU.....
  ....tyyyyyyyyyyyyynnnnnnnnnnnnnNNNNNNNNNNNNNnnYYYYYYYYYYYUUU....
  ....ttyyyyyyyyyynnNNNNNNNNNNNNNNNNNNNNNNNNNNNNnnYYYYYYYYYUUU....
  ....tyyyyyyyyyyynnNNNNNNNNNNNNNNNNNNNNNNNNNNNNnnYYYYYYYYttUU....
  ....yyyyyyyyyyyynnNNNNNNNNNNNNNNNNNNNNNNNNNNNNnnYYYYYYYYYUUU....
  .....yyttyyyyyyyynnNNNNNNNNNNNNNNNNNNNNNNNNNNnnYYYYYYYYYUUU.....
  ......yyyyyyyyyyyyynnNNNNNNNNNNNNNNNNNNNNNNnnYYYYYYYttYUUU......
  ........yyyyttyyyyyyyyynnNNNNNNNNNNNNNNnnYYYYYYYttYYYUUU........
  ...........yyyyyyyyyccbbbbbbbbbbbbbbbbbbBBBBYYYYYYUUU...........
  ....................cbbbbbbbbbbbbbbbbbbBBBBB....................
  .....................cbbbbbbbbbbbbbbbbBBBBB.....................
  .....................BBBBBBBBBBBBBBBBBBBBBB.....................
  .....................yyyyyyyYYYYYYYYYYYUUUU.....................
  ......................yyyyyyYYUUYYYYYYUUUU......................
  ......................yyyyyYYYYYYYYYYYUUUU......................
  .......................yyyyUUYYYYYUUYUUUU.......................
  .........................yyyYYYYYYYUUUU.........................
  ............................yyYYUUUU............................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
  ................................................................
`);

export const HAT_RIBBON = grid(`
  ...........cbB
  .........ccbB.
  .......ccbbB..
  ....cccbbBB...
  ..ccbbbBB.....
  .cbbbBB.......
  cbbBB.........
  cB.bB.........
  c...B.........
`);

/** The hat's frames in the art's own colours, before the outline: `fly` 4f, `rest` 1f (64×40). */
export const HAT_FRAMES = {
  fly: [hatAt(4), tipped(14), HAT_DOWN, tipped(-14)],
  rest: [
    frame(
      (c) => {
        c.blit(HAT_RIBBON, 2, 24);
        c.blit(HAT, 0, 6);
      },
      64,
      40,
    ),
  ],
};

/**
 * A hat's `frames` (the sun hat's `HAT_FRAMES`, or headwear's own; none for headwear tied on) in a hat's `palette` on a
 * `team` and outlined (64×40). The rim follows the straw (and the team's ribbon), as on the body.
 */
export function hatFrames(
  frames: Record<'fly' | 'rest', readonly PixelCanvas[]>,
  palette: Recolor,
  team: Recolor,
): Record<'fly' | 'rest', PixelCanvas[]> {
  const done = (c: PixelCanvas) => {
    const key = remap(c.clone(), team);
    const out = remap(key.clone(), palette);
    selOutline(out, key);
    return out;
  };
  return { fly: frames.fly.map(done), rest: frames.rest.map(done) };
}
